// Évaluations annuelles — shared template + types + pure helpers.
// Ported from the validated prototype in design (ev-data.jsx). The
// criteria grid is the SHIFTIN evaluation template (4 blocks, weighted
// coefficients, 1→5 scale); notes are stored server-side as a JSON map
// keyed by criterion id so template evolutions never need a migration.
// ─── Cycle ───────────────────────────────────────────────────
// One evaluation campaign per calendar year. Dates for the 2026
// campaign come from the validated design; future campaigns get a
// computed default (same calendar rhythm) until tuned.
import { EVAL_ROLE_TEMPLATES } from './evalTemplates.js';
export function evalCycle(year) {
    return {
        year,
        lbl: `Évaluation annuelle ${year}`,
        periode: `Août ${year - 1} → Juillet ${year}`,
        dAuto: `${year}-08-07`,
        dMgr: `${year}-08-14`,
        otoStart: `${year}-08-17`,
        otoEnd: `${year}-08-28`,
        otoWin: '17 → 28 août',
        close: `${year}-08-31`,
    };
}
// Campaign timeline (display only — statuses are derived client-side
// from today's date + cycle deadlines).
export const EVAL_PHASES = [
    { id: 'obj', lbl: 'Objectifs fixés', dt: '12 jan' },
    { id: 't1', lbl: 'Check-in T1', dt: '10 avr' },
    { id: 't2', lbl: 'Check-in T2', dt: '10 juil' },
    { id: 'auto', lbl: 'Auto-évaluations', dt: '→ 7 août' },
    { id: 'mgr', lbl: 'Évals manager', dt: '→ 14 août' },
    { id: 'oto', lbl: 'Entretiens 1:1', dt: '17–28 août' },
    { id: 'clo', lbl: 'Clôture', dt: '31 août' },
];
// ─── Scale ───────────────────────────────────────────────────
// Half-point scale — the 2026 submission workbooks use every half step
// from 1 to 5 (incl. 1,5 and 4,5), so the app accepts them all.
export const EVAL_SCALE = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5];
export const EVAL_SCALE_LBL = {
    1: 'Très insuffisant',
    1.5: 'Insuffisant −',
    2: 'Insuffisant',
    2.5: 'Moyen',
    3: 'Bien',
    3.5: 'Solide',
    4: 'Très bien',
    4.5: 'Très bien +',
    5: 'Excellent',
};
export const EVAL_BLOCKS = [
    { id: 'perf', lbl: 'Performance professionnelle', crits: [
            { id: 'business', lbl: 'Contribution au business', short: 'Business', coef: 3, ds: "Génération directe de business pour l'agence : pitchs gagnés, opportunités créées et comptes développés.", beh: ['Va chercher les opportunités au lieu de les attendre', 'Détecte les upsells / cross-sells et les transforme en propositions signées', 'Intègre le ROI dans ses recommandations'] },
            { id: 'expertise', lbl: 'Expertise métier', short: 'Expertise', coef: 2, ds: "Référence métier du département, garant des méthodologies et des standards de l'agence.", beh: ['Fait figure de référence métier', 'Dernier filet de qualité sur les dossiers stratégiques', "Fait progresser l'expertise collective"] },
            { id: 'projets', lbl: 'Gestion de projets', short: 'Projets', coef: 2, ds: 'Supervision du portefeuille de projets, arbitrages de ressources et gestion des escalades.', beh: ["Pilote les projets à fort enjeu sans être le goulot d'étranglement", 'Arbitre priorités et ressources entre les comptes', 'Garantit la traçabilité des décisions clés'] },
            { id: 'qualite', lbl: 'Qualité des livrables', short: 'Qualité', coef: 2, ds: 'Garant du standard de qualité du département sur tout output envoyé au client.', beh: ["Rien ne part au client sous le standard de l'agence", 'Met en place relecture et contrôle qualité', "Élève le niveau d'exigence par ses retours"] },
            { id: 'prod', lbl: "Productivité & rapidité d'exécution", short: 'Prod', coef: 1.5, ds: 'Organisation de sa charge entre pilotage, management et production à enjeu, au rythme agence.', beh: ["Maintient le rythme d'exécution attendu", "Débloque rapidement ce qui freine l'équipe", 'Gère les urgences sans désorganiser le reste'] },
            { id: 'outils', lbl: 'Outils opérationnels', short: 'Outils', coef: 1.5, ds: "Exemplarité sur les outils, fiabilité du suivi et structuration de l'outillage du département.", beh: ['Weekly status, contact reports et fiches remplis dans les délais', 'Trackers et tableaux de bord fiables', 'Organisation documentaire propre sur Drive'] },
            { id: 'objectifs', lbl: 'Atteinte des objectifs', short: 'Objectifs', coef: 3, ds: 'Réalisation de ses objectifs individuels et pilotage des objectifs collectifs du département.', beh: ['Atteint les objectifs fixés pour la période', "Corrige la trajectoire en cours d'année", "Fait preuve d'engagement dans son développement"] },
        ] },
    { id: 'eng', lbl: 'Engagement & posture professionnelle', crits: [
            { id: 'fiabilite', lbl: 'Fiabilité & responsabilité', short: 'Fiabilité', coef: 2, ds: 'Respect des engagements, transparence et règle du « no surprise » vis-à-vis de la direction.', beh: ['Tient ses engagements sans relances', "Alerte AVANT que le problème n'éclate", 'Assume ses erreurs et met en place des correctifs durables'] },
            { id: 'rigueur', lbl: 'Rigueur & discipline', short: 'Rigueur', coef: 1.5, ds: 'Ponctualité, assiduité et exemplarité sur les règles internes.', beh: ["À l'heure au travail et aux réunions", 'Respecte pointage, procédures, conventions', "Comportement de référence pour l'équipe"] },
            { id: 'flex', lbl: 'Flexibilité & disponibilité', short: 'Flexibilité', coef: 1, ds: "Engagement soutenu tout au long de l'année et disponibilité lors des pics de charge.", beh: ['Disponible en période de forte charge (pitchs, urgences)', 'Accepte les ajustements ponctuels', 'Prend des sujets hors périmètre quand il le faut'] },
            { id: 'fidelite', lbl: 'Fidélité & engagement', short: 'Fidélité', coef: 1, ds: "Engagement durable envers l'agence, ses valeurs et sa culture.", beh: ['Ancienneté valorisée (barème 1→5)', 'Participe à la mémoire organisationnelle', "Contribue positivement à la culture d'agence"] },
        ] },
    { id: 'perso', lbl: 'Compétences personnelles & relationnelles', crits: [
            { id: 'collab', lbl: "Collaboration & travail d'équipe", short: 'Collab', coef: 1.5, ds: 'Qualité de la coopération avec les autres départements et la direction.', beh: ['Collaboration fluide avec Créa, Digital, Admi et Dev', 'Feedback positif des pairs et de la Direction', "Fait circuler l'information utile"] },
            { id: 'decision', lbl: 'Prise de décision & exemplarité', short: 'Décision', coef: 1.5, ds: 'Qualité et rapidité de la prise de décision, exemplarité au quotidien.', beh: ["Décide vite et bien, y compris dans l'ambiguïté", 'Tranche les arbitrages difficiles', "Donne l'exemple par son éthique de travail"] },
            { id: 'initiative', lbl: "Esprit d'initiative & résolution de problèmes", short: 'Initiative', coef: 1.5, ds: 'Identification des dysfonctionnements, proposition de solutions et amélioration continue.', beh: ["N'arrive jamais avec un problème sans options", "N'attend pas d'instruction quand un problème est visible", "Passe de la solution à l'action"] },
            { id: 'apprentissage', lbl: 'Apprentissage & progression', short: 'Progression', coef: 1, ds: "Animation d'une culture d'apprentissage et développement continu de ses compétences.", beh: ['Anime des sessions de partage', 'Lit au moins un livre/an de la bibliothèque agence', 'Suit au moins une formation/an'] },
            { id: 'veille', lbl: 'Veille & partage de connaissances', short: 'Veille', coef: 1, ds: 'Structuration de la veille du département et contribution à la veille collective.', beh: ['Partage des contenus qualitatifs sur #veille', 'Anime la veille du département', 'Fait monter les juniors par le partage'] },
            { id: 'attitude', lbl: "Attitude & impact sur l'environnement", short: 'Attitude', coef: 1.5, ds: 'Comportement professionnel, gestion du stress et influence sur la dynamique du département.', beh: ['Son attitude donne le ton au département', 'Accepte les retours sans les prendre personnellement', 'Concentré sur les solutions, pas les problèmes'] },
            { id: 'vie', lbl: "Vie d'agence & esprit collectif", short: 'Collectif', coef: 1, ds: "Participation à la vie collective, état d'esprit positif.", beh: ['Participe aux initiatives collectives', 'Attitude positive pendant les surcharges', 'Remonte ses désaccords à sa hiérarchie au lieu de les propager'] },
        ] },
    { id: 'spec', lbl: 'Compétences spécifiques — poste', crits: [
            { id: 'strat', lbl: 'Approche stratégique', short: 'Stratégie', coef: 3, ds: "Standard stratégique du département, élévation des recommandations et ownership de la méthodologie.", beh: ['Challenge et élève les recos avant tout envoi client', 'Transforme les briefs complexes en visions claires', "Porte le S.H.I.F.T. Framework™ et en garantit l'adoption"] },
            { id: 'relation', lbl: 'Relation & satisfaction client', short: 'Client', coef: 3, ds: 'Relations au plus haut niveau, gestion des comptes à risque et pilotage de la satisfaction.', beh: ['Règle des 3W : never Wonder, Wait or Worry', 'Détecte les comptes à risque et pilote les plans de sauvetage', 'Gère escalades, crises et annonces difficiles'] },
            { id: 'renta', lbl: 'Rentabilité & pilotage financier', short: 'Rentabilité', coef: 2.5, ds: "Rentabilité du portefeuille, maîtrise de l'overservicing et fiabilité du forecast.", beh: ["Facture les dépassements de scope au lieu de les absorber", 'Forecast hebdo fiable', 'Arbitre les ressources selon la rentabilité'] },
            { id: 'newbiz', lbl: 'Développement business & pitchs', short: 'New biz', coef: 2.5, ds: 'Pipeline new business, pilotage des pitchs et activation des relais de croissance.', beh: ['Anime un pipeline new business actif', 'Pilote les pitchs de bout en bout, debrief documenté', 'Améliore le taux de transformation dans la durée'] },
            { id: 'mgmt', lbl: 'Management', short: 'Management', coef: 3, ds: "Aptitudes managériales, développement des équipes, pilotage de la performance et de l'engagement.", beh: ['O3s réguliers et structurés avec chaque membre', 'Feedback régulier, constructif et actionnable', 'Mène les conversations difficiles sans les repousser', 'Owning the inputs de son équipe'] },
        ] },
];
export const EVAL_CRITS = EVAL_BLOCKS.flatMap((b) => b.crits.map((c) => ({ ...c, block: b.id })));
// ─── Role templates ──────────────────────────────────────────
// One criteria grid per role (imported from the 2026 submission
// workbooks, see evalTemplates.ts) + the generic grid above as the
// fallback for roles without a dedicated template. Notes are stored
// keyed by criterion id, so an evaluation is always read with the
// template stamped on its row.
export const EVAL_TEMPLATE_GENERIC = {
    code: 'GENERIC',
    lbl: 'Grille générale SHIFTIN',
    postes: [],
    blocks: EVAL_BLOCKS,
};
export const EVAL_TEMPLATES = [...EVAL_ROLE_TEMPLATES, EVAL_TEMPLATE_GENERIC];
export function evalTemplate(code) {
    return EVAL_TEMPLATES.find((t) => t.code === code) ?? EVAL_TEMPLATE_GENERIC;
}
/** Flat criteria (with block id) of a template. */
export function evalTemplateCrits(code) {
    return evalTemplate(code).blocks.flatMap((b) => b.crits.map((c) => ({ ...c, block: b.id })));
}
const evalNorm = (t) => t.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
/** Pick the template for an employee's poste/fonction (GENERIC fallback). */
export function evalTemplateForPoste(poste) {
    if (!poste)
        return 'GENERIC';
    const n = evalNorm(poste);
    for (const t of EVAL_ROLE_TEMPLATES) {
        if (t.postes.some((p) => n.includes(p)))
            return t.code;
    }
    return 'GENERIC';
}
/** Radar axes — the template's highest-coefficient criteria (top 7). */
export function evalRadarAxes(code) {
    const crits = evalTemplateCrits(code);
    return [...crits]
        .map((c, i) => ({ c, i }))
        .sort((a, b) => b.c.coef - a.c.coef || a.i - b.i)
        .slice(0, 7)
        .sort((a, b) => a.i - b.i)
        .map((x) => x.c);
}
// Radar axes of the generic grid (kept for reference/back-compat).
export const EVAL_RADAR_AXES = ['business', 'objectifs', 'strat', 'relation', 'mgmt', 'renta', 'newbiz'];
// ─── Bilan qualitatif (auto-évaluation) ──────────────────────
export const EVAL_BILAN_QS = [
    'Quel bilan tirez-vous de votre activité personnelle cette année ?',
    'Quelles ont été vos plus grandes réalisations ?',
    'Quels ont été les échecs ou points de déception ?',
    'Quelles difficultés avez-vous rencontrées ? Quelles solutions proposez-vous ?',
    "Quels sont vos objectifs pour l'année prochaine ?",
];
// ─── Objectifs ───────────────────────────────────────────────
export const EVAL_OBJ_CATS = [
    'Contribution business',
    'Excellence client',
    'Expertise métier',
    'Développement de compétences',
    'Amélioration des process',
    'Développement personnel',
];
export const EVAL_QUARTERS = ['T1', 'T2', 'T3', 'T4'];
export const EVAL_QDATES = { T1: '10 avr', T2: '10 juil', T3: '9 oct', T4: '8 jan' };
export const OBJECTIVE_STATUS_LBL = {
    ontrack: 'On track',
    risk: 'À risque',
    off: 'Off track',
};
// Guide SMART — « bon vs mauvais objectif ».
export const EVAL_GUIDE_BVM = [
    ['« Améliorer mes compétences »', "« Maîtriser l'outil Freedcamp de manière autonome d'ici juin 2026, validé par mon manager »"],
    ['« Être plus organisé »', "« Remplir 100% des weekly status dans les délais pendant 3 mois consécutifs d'ici septembre 2026 »"],
    ['« Mieux gérer les clients »', '« Réduire à zéro les remarques client répétées sur les mêmes erreurs sur 2 mois consécutifs »'],
    ['« Faire plus de business »', "« Générer au moins 3 opportunités d'upsell formalisées et présentées au client d'ici décembre 2026 »"],
];
export const EVAL_GUIDE_LEVELS = {
    'Account Executive': [
        { cat: 'Contribution business', title: "Générer au moins 2 opportunités d'upsell identifiées et présentées au client", kpi: "Nombre d'opportunités présentées ≥ 2", deadline: 'Décembre 2026' },
        { cat: 'Excellence client', title: "Zéro livrable envoyé en retard sans alerte préalable sur l'année", kpi: "0 retard non signalé sur l'année", deadline: 'Décembre 2026' },
        { cat: 'Expertise métier', title: "Maîtriser l'outil de gestion de projet de l'agence de manière autonome", kpi: 'Utilisation autonome validée par le manager', deadline: 'Juin 2026' },
        { cat: 'Développement personnel', title: 'Améliorer ma prise de parole : présenter 3 recommandations en réunion client', kpi: '3 présentations client réalisées', deadline: 'Décembre 2026' },
    ],
    'Sr Account Executive': [
        { cat: 'Contribution business', title: "Identifier et formaliser au moins 3 opportunités d'upsell sur mes comptes", kpi: '≥ 3 propositions formalisées', deadline: 'Décembre 2026' },
        { cat: 'Excellence client', title: 'Taux de satisfaction positif sur 100% de mes comptes', kpi: '0 plainte formelle', deadline: 'Décembre 2026' },
        { cat: 'Expertise métier', title: 'Gérer avec succès au moins 1 projet multi-canal', kpi: '1 projet multi-canal livré dans les délais', deadline: 'Décembre 2026' },
        { cat: 'Développement de compétences', title: 'Suivre une formation en stratégie de marque', kpi: 'Certification ou attestation obtenue', deadline: 'Novembre 2026' },
    ],
    'Account Manager': [
        { cat: 'Contribution business', title: 'Générer un CA additionnel de X% sur mon portefeuille via upsell/cross-sell', kpi: 'CA additionnel ≥ X% vs N-1', deadline: 'Décembre 2026' },
        { cat: 'Excellence client', title: 'Améliorer le score de satisfaction client sur mes comptes', kpi: 'Score en hausse vs année précédente', deadline: 'Décembre 2026' },
        { cat: 'Développement de compétences', title: 'Accompagner 1 profil junior via un mentoring structuré', kpi: '1 junior accompagné avec objectifs et suivi', deadline: 'Décembre 2026' },
        { cat: 'Amélioration des process', title: 'Réduire les dépassements de deadline de X% sur mes projets', kpi: 'Taux de respect des délais ≥ X%', deadline: 'Décembre 2026' },
    ],
    'Account Director': [
        { cat: 'Contribution business', title: 'Augmenter le CA global du département de X% vs N-1', kpi: 'CA département +X% vs N-1', deadline: 'Décembre 2026' },
        { cat: 'Contribution business', title: 'Piloter au moins 4 pitchs stratégiques avec un win rate ≥ X%', kpi: '≥ 4 pitchs pilotés, debriefs documentés', deadline: 'Décembre 2026' },
        { cat: 'Excellence client', title: 'Taux de rétention client de X% : zéro perte sur motif qualité/relation', kpi: 'Rétention ≥ X%, 0 départ qualité', deadline: 'Décembre 2026' },
        { cat: 'Expertise métier', title: "Former 100% de l'équipe au S.H.I.F.T. Framework™ et au Shift Test™", kpi: '100% formés, outils utilisés sur les pitchs', deadline: 'Octobre 2026' },
        { cat: 'Développement de compétences', title: 'Faire émerger un relais N-1 sur les sujets courants du département', kpi: "Continuité assurée sur 2 semaines d'absence", deadline: 'Décembre 2026' },
    ],
};
// ─── Pure helpers ────────────────────────────────────────────
export const EVAL_COEF_SUM = EVAL_CRITS.reduce((a, c) => a + c.coef, 0);
/** Snap an arbitrary value to the nearest scale step. */
export function evalSnap(v) {
    return EVAL_SCALE.reduce((a, b) => (Math.abs(b - v) < Math.abs(a - v) ? b : a));
}
/** Weighted global note + completion for a notes map (template crits). */
export function evalWeighted(notes, crits = EVAL_CRITS) {
    let s = 0;
    let c = 0;
    let n = 0;
    for (const cr of crits) {
        const v = notes?.[cr.id];
        if (v != null) {
            s += v * cr.coef;
            c += cr.coef;
            n++;
        }
    }
    return { note: c ? s / c : 0, filled: n, total: crits.length, pct: crits.length ? Math.round((n / crits.length) * 100) : 0 };
}
/** Weighted average of one block; null when nothing noted. */
export function evalBlockAvg(notes, blockId, crits = EVAL_CRITS) {
    let s = 0;
    let c = 0;
    for (const cr of crits) {
        if (cr.block !== blockId)
            continue;
        const v = notes?.[cr.id];
        if (v != null) {
            s += v * cr.coef;
            c += cr.coef;
        }
    }
    return c ? s / c : null;
}
/** Per-criterion gaps (mgr − auto), biggest absolute gap first. */
export function evalGapList(auto, mgr, crits = EVAL_CRITS) {
    return crits
        .filter((c) => auto[c.id] != null && mgr[c.id] != null)
        .map((c) => ({ crit: c, a: auto[c.id], m: mgr[c.id], d: mgr[c.id] - auto[c.id] }))
        .sort((x, y) => Math.abs(y.d) - Math.abs(x.d));
}
/** Validate a notes map: known criterion ids, values on the scale. */
export function evalValidNotes(notes, crits = EVAL_CRITS) {
    if (notes == null || typeof notes !== 'object' || Array.isArray(notes))
        return false;
    const known = new Set(crits.map((c) => c.id));
    for (const [k, v] of Object.entries(notes)) {
        if (!known.has(k))
            return false;
        if (typeof v !== 'number' || !EVAL_SCALE.includes(v))
            return false;
    }
    return true;
}
/** Aggregate workflow step (mirrors the prototype's evStepOf). */
export function evalStepOf(e) {
    if (e.finalDone)
        return 'done';
    if (e.otoChosen)
        return 'oto';
    if (e.autoStatus === 'SUBMITTED' && e.mgrStatus === 'SUBMITTED')
        return 'gap';
    if (e.autoStatus === 'SUBMITTED' || e.mgrStatus === 'SUBMITTED')
        return 'partial';
    if (e.autoStatus === 'DRAFT')
        return 'draft';
    return 'none';
}
//# sourceMappingURL=evaluations.js.map